import { css, cx } from '@linaria/core'
import type { FC, ReactNode } from 'react'
import { Fragment, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { isRouteErrorResponse, useRouteError } from 'react-router-dom'

import { Button } from '#components/Button'
import { Icon } from '#components/Icon'
import { Spinner } from '#components/Spinner'
import { PageHeaderContent } from '#features/AppLayout'
import { flattenError } from '#features/ErrorHandling/errorHelpers'
import ChevronIcon from '#icons/chevron-1-right-16.svg'
import ErrorIcon from '#icons/close-cirlce-24.svg'
import { fragments, style } from '#styles'
import 'react-router-dom'
import { ErrorReportButton } from '#features/ErrorHandling/ErrorReportButton'
import { RoutingError } from '#features/ErrorHandling/RoutingError'
import { AuthenticationError } from '#features/User/AuthenticationError'
import { resetSessionState, signOutSilent, userManager } from '#features/User/userManager'
import { useHomeNavigation } from '#utils/useHomeRouting'

const inlineErrorDisplayClass = css`
  width: 100%;
  max-width: 980px;
  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8.5px;
  
  --color-background: ${style.theme.backgroundSurfacePrimaryStrong};
  --color-box-background: ${style.theme.backgroundSurfaceTertiary};
  
  &.in-box {
    --color-background: ${style.theme.backgroundSurfaceTertiary};
    --color-box-background: ${style.theme.backgroundSurfacePrimaryStrong};
  }
  
  > .error-icon {
    background-color: ${style.theme.backgroundSurfaceAccentDefault};
    position: sticky;
    top: 0;
  }
  
  > div.error-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
    flex: 1;
    width: calc(100% - 60px);
    
    > header {
      position: sticky;
      top: 0;
      z-index: 1;
      background-color: var(--color-background);
      padding-top: 3px;

      > .title {
        ${fragments.textStyle.heading.l.regular}
      }

      > .subtitle {
        ${fragments.textStyle.heading.l.regular}
      }

      padding-bottom: 20px;
    }

    > footer {
      position: sticky;
      bottom: 0;
      z-index: 1;
      background-color: var(--color-background);
      padding-top: 20px;
      gap: 20px;
      display: flex;
      flex-direction: row;
      align-items: flex-end;
      justify-content: space-between;

      .button-row {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
        gap: 10px;

        > .button {
          display: flex;
          gap: 8px;
          flex-direction: row;
          align-items: center;
        }
      }
    }
  }


  &.kind-inline {
    min-height: 100%;
    
    > .error-icon {
      width: 34px;
      height: 34px;
    }

    > div.error-content {
      min-height: 100%;
      gap: 12px;
      
      > header {
        > .title {
          ${fragments.textStyle.heading.s.regular}
        }

        > .subtitle {
          ${fragments.textStyle.heading.s.subtle}
        }
      }
      
      > footer {
        margin-top: auto;
      }
    }
  }
  
  &.kind-sidebar {
    min-height: 100%;
    
    > .error-icon {
      width: 34px;
      height: 34px;
    }

    > div.error-content {
      min-height: 100%;
      gap: 8px;
      
      > header {
        > .title {
          ${fragments.textStyle.heading.xs.regular}
        }

        > .subtitle {
          ${fragments.textStyle.heading.xs.subtle}
        }
      }
      
      summary > span {
        -webkit-line-clamp: 3;
      }

      > footer {
        margin-top: auto;
        flex-direction: column;
      }
    }
  }
`

const errorDetailsClass = css`
  overflow: hidden;
  
  > summary {
    padding: 10px;
    color: ${style.theme.textInverse};
    background: ${style.theme.backgroundSurfaceAccentDefault};
    ${fragments.textStyle.heading.xs.regular}
    appearance: none;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    
    > ._open {
      transition: transform 0.2s ease-in-out;
      align-self: flex-start;
      margin-top: 2px;
    }
    
    > span {
      flex: 1;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;  
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 150%;
    }
    
    &:hover {
      background: ${style.theme.backgroundSurfaceAccentStrong};
    }
    
    &::marker {
      display: none;
      content: '';
    }
  }

  &[open] > summary > ._open {
    transform: rotate(90deg);
  }
  
  > div {
    > pre {
      min-height: 3em;
      overflow-y: auto;
      font-family: monospace;
      background: var(--color-box-background);
      color: ${style.theme.textSoft};
      padding: 10px;
      user-select: text;
      margin-bottom: 5px;  
      white-space: pre-wrap;
    }
  
    > h4 {
      ${fragments.textStyle.heading.s.subtle}
      margin-bottom: 4px;
      margin-top: 16px;
    }
  }
  
  .kind-inline & {
    > summary {
      ${fragments.textStyle.body.s.condensedBold}
    }
    
    > div > h4 {
      ${fragments.textStyle.heading.xs.subtle}
    }
  }
  
  .kind-sidebar & {    
    > summary {
      ${fragments.textStyle.body.s.condensedBold}
    }
    
    > div > h4 {
      ${fragments.textStyle.heading.xs.subtle}
    }
  }
`

interface InlineErrorDisplayProps {
  error: unknown
  className?: string
  title?: string
  subtitle?: string
  primaryErrorMessage?: string
  kind?: ErrorDisplayKind
  actions?: ReactNode
}

type ErrorDisplayKind = 'full' | 'inline' | 'sidebar'

export const InlineErrorDisplay: FC<InlineErrorDisplayProps> = ({
  error,
  className,
  title,
  subtitle,
  primaryErrorMessage,
  kind = 'full',
  actions,
}) => {
  const navigate = useHomeNavigation()
  const { t } = useTranslation('shared')

  const errorInfo = flattenError(error)

  const onBackClick = () => {
    navigate()
  }

  const isAuthenticationError = error instanceof AuthenticationError
  const displayedErrorMessage = (
    primaryErrorMessage ??
    errorInfo[0]?.message ??
    t('error-handler.default-error-message', 'An unexpected error occurred.')
  ).replace('\\n', '\n\n')

  return (
    <div className={cx(inlineErrorDisplayClass, className, `kind-${kind}`)}>
      <Icon iconUrl={ErrorIcon} className="error-icon" size="xl" />

      <div className="error-content">
        <header>
          <p className="title">{title ?? t('error-handler.title', 'Apologies for the hiccup…')}</p>
          {subtitle && <p className="subtitle">{subtitle}</p>}
        </header>

        <details className={errorDetailsClass}>
          <summary>
            <Icon iconUrl={ChevronIcon} className="_open" />
            <span className="_main-error">{displayedErrorMessage.replace('\\n', '\n\n')}</span>
          </summary>
          <div>
            {errorInfo.map((e, i) => {
              return (
                <Fragment key={`e_${e.name}_${i}`}>
                  <h4>{e.name}</h4>
                  {e.message && <pre>{e.message}</pre>}
                  {e.stack && <pre>{e.stack}</pre>}
                  {e.data && <pre>{e.data}</pre>}
                </Fragment>
              )
            })}
          </div>
        </details>

        <footer>
          <div className="help-text">
            <h4>{t('error-handler.help-title', 'What can I do?')}</h4>
            {t('error-handler.help-text', 'Please report this error to help us improve the application.')}
          </div>

          <div className="button-row">
            {actions ? (
              actions
            ) : isAuthenticationError ? (
              <Button
                className="button secondary"
                onClick={() => {
                  const authPathNames = ['/login', '/auth', '/signed-out']

                  if (authPathNames.some((p) => window.location.pathname.startsWith(p))) {
                    // if we are on an auth related path, reset state & go to home instead of reloading, let the routing handle it
                    try {
                      void signOutSilent()
                      void userManager.clearStaleState()
                      resetSessionState()
                    } catch {
                      // ignore
                    }
                    window.location.href = '/'
                    return
                  }
                  window.location.href = String(window.location.href)
                }}
              >
                {t('common.button.retry')}
              </Button>
            ) : (
              <Button className="button secondary" onClick={onBackClick}>
                {t('common.button.back-to-home', 'Back to Home')}
              </Button>
            )}

            <ErrorReportButton errorInfo={errorInfo} />
          </div>
        </footer>
      </div>
    </div>
  )
}

const errorDisplayClass = css`
  max-height: calc((100vh * var(--inverse-zoom)) - ${style.constants.headerHeightVariable});
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow-x: hidden;
  padding-top: 60px;
  padding-bottom: 60px;
`

/**
 * View to display general error page contents
 */
export const ErrorDisplayPage: FC<{ hideHeader?: boolean; headerContent?: ReactNode }> = ({ hideHeader, headerContent }) => {
  const error = useRouteError()
  const fragment = isRouteErrorResponse(error) ? <RoutingError error={error} /> : <InlineErrorDisplay error={error} />
  return (
    <main className={errorDisplayClass}>
      <Suspense fallback={<Spinner size="xl" />}>
        {!hideHeader && headerContent && <PageHeaderContent>{headerContent}</PageHeaderContent>}
        {fragment}
      </Suspense>
    </main>
  )
}
